/**
 * Pure helpers for the shared <AgentNew scope> component (PR-B).
 *
 * Two import-payload parsers (one per scope) — extracted so they can be
 * unit-tested without React. The sysadmin parser was already exported
 * from the pre-refactor route file (per round-8 review feedback); the
 * tenant parser is hoisted from `handleImport` for the same reason.
 *
 * `slugify` is re-exported from the AgentBuilder shared helpers — it's
 * the canonical implementation and there's no value in duplicating it.
 */
import { slugify } from '@/components/agent-builder-shared/AgentBuilder.helpers';
import type { CreateAgentInput } from '@/lib/use-agents';
import type { CreateCatalogAgentInput } from '@/lib/use-sysadmin-catalog-agents';

export { slugify };

// ─── Shared string-array coercion ──────────────────────────────────────────

function stringArray(v: unknown): string[] | undefined {
  return Array.isArray(v)
    ? v.filter((t): t is string => typeof t === 'string')
    : undefined;
}

// ─── Tenant import parser ───────────────────────────────────────────────────

/**
 * Parse + normalise an Import-from-JSON payload into the tenant
 * create-input shape. Returns `null` when JSON is invalid or the
 * required fields (`name`, `systemPrompt`) are missing/blank.
 *
 * Mirrors the original inline logic that lived inside
 * routes/agents/new.tsx::handleImport — hoisting was deferred there
 * because there was no test surface; PR-B fixes that.
 */
export function parseTenantImportPayload(raw: string): CreateAgentInput | null {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  const importedName = typeof parsed.name === 'string' ? parsed.name : '';
  const importedPrompt =
    typeof parsed.systemPrompt === 'string' ? parsed.systemPrompt : '';
  if (!importedName.trim() || !importedPrompt.trim()) return null;

  const body: CreateAgentInput = {
    name: importedName,
    systemPrompt: importedPrompt,
    description:
      typeof parsed.description === 'string' ? parsed.description : undefined,
    category:
      typeof parsed.category === 'string' ? parsed.category : undefined,
    icon: typeof parsed.icon === 'string' ? parsed.icon : undefined,
    tools: stringArray(parsed.tools),
    tags: stringArray(parsed.tags),
    triggerModes: stringArray(parsed.triggerModes),
  };
  return body;
}

// ─── Sysadmin import parser ─────────────────────────────────────────────────

/**
 * Parse + normalise an Import-from-JSON payload into the sysadmin
 * catalog create-input shape. Returns `null` on missing required
 * fields.
 *
 * Preserves the pre-refactor key precedence: legacy keys (`tools`,
 * `functions`, `skills`, `workflows`) win over canonical ones
 * (`toolSlugs`, …) because hand-written exports from tenant use the
 * legacy shape.
 */
export function parseSysadminImportPayload(
  raw: string,
): CreateCatalogAgentInput | null {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  const importedName = typeof parsed.name === 'string' ? parsed.name.trim() : '';
  const importedPrompt =
    typeof parsed.systemPrompt === 'string' ? parsed.systemPrompt.trim() : '';
  if (!importedName || !importedPrompt) return null;

  const importedSlug =
    typeof parsed.slug === 'string' && parsed.slug.trim()
      ? parsed.slug.trim()
      : slugify(importedName);

  const body: CreateCatalogAgentInput = {
    slug: importedSlug,
    displayName: importedName,
    description:
      typeof parsed.description === 'string' ? parsed.description : importedName,
    systemPrompt: importedPrompt,
    category: typeof parsed.category === 'string' ? parsed.category : 'custom',
    toolSlugs: stringArray(parsed.tools) ?? stringArray(parsed.toolSlugs) ?? [],
    functionKeys:
      stringArray(parsed.functions) ?? stringArray(parsed.functionKeys) ?? [],
    skillSlugs:
      stringArray(parsed.skills) ?? stringArray(parsed.skillSlugs) ?? [],
    workflowSlugs:
      stringArray(parsed.workflows) ?? stringArray(parsed.workflowSlugs) ?? [],
  };
  return body;
}

// ─── Quick-create body builders ─────────────────────────────────────────────

/**
 * Build the tenant /custom-agents POST body from the Quick Create form
 * values. Description is omitted when blank (matches pre-refactor).
 */
export function buildTenantQuickBody(values: {
  name: string;
  description: string;
  category: string;
  systemPrompt: string;
}): CreateAgentInput {
  return {
    name: values.name.trim(),
    description: values.description.trim() || undefined,
    systemPrompt: values.systemPrompt.trim(),
    category: values.category,
  };
}

/**
 * Build the sysadmin /admin/platform/catalog/agents POST body from the
 * Quick Create form values. Slug auto-derives from name when blank;
 * description defaults to the trimmed name to satisfy the catalog
 * schema (description is required there).
 */
export function buildSysadminQuickBody(values: {
  name: string;
  slug: string;
  description: string;
  category: string;
  systemPrompt: string;
}): CreateCatalogAgentInput {
  const finalSlug = (values.slug.trim() || slugify(values.name)).trim();
  return {
    slug: finalSlug,
    displayName: values.name.trim(),
    description: values.description.trim() || values.name.trim(),
    systemPrompt: values.systemPrompt.trim(),
    category: values.category,
  };
}
